import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Megaphone, Trash2 } from 'lucide-react';
import { Dropzone } from './Dropzone';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (!src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`);

const defaults = { id: 'banner', image: '', alt_text: '', link_url: '', published: false };

const BannerManager = () => {
  const [data, setData] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/banner');
      if (res.data && res.data.id) setData({ ...defaults, ...res.data });
    } catch (e) {
      console.error('load banner', e);
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:banner',
    data,
    enabled: loaded,
    savedAt: data.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setData({ ...defaults, ...draft });
    discardDraft();
  };

  const save = async () => {
    setLoading(true);
    try {
      await adminApi.post('/banner', data);
      const res = await adminApi.get('/banner');
      if (res.data) setData({ ...defaults, ...res.data });
      markSaved(res.data || data);
      toast({ title: 'Saved', description: 'Announcement banner updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => setData((d) => ({ ...d, image: '' }));
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="space-y-6" data-testid="banner-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="banner-draft-banner"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-[#1565C0]" />
          <h2 className="text-2xl font-bold">Homepage Announcement Banner</h2>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="banner-published" className="text-sm">Published</Label>
            <Switch
              id="banner-published"
              checked={data.published}
              onCheckedChange={(v) => set('published', v)}
              data-testid="banner-published-switch"
            />
          </div>
          <HistoryDialog collection="banner" docId="banner" onRestored={load} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card>
          <CardHeader><CardTitle>Edit Banner</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="font-semibold">Banner Image</Label>
              {data.image && (
                <div className="mt-2 relative">
                  <img src={resolveImg(data.image)} alt="Banner preview" className="w-full rounded border" data-testid="banner-image-preview" />
                  <Button
                    variant="destructive" size="sm" className="absolute top-2 right-2"
                    onClick={removeImage} data-testid="banner-remove-image-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />Remove
                  </Button>
                </div>
              )}
              <div className="mt-2">
                <Dropzone compact onUpload={(url) => set('image', url)} label="Drop banner image (wide rectangle recommended)" />
              </div>
            </div>

            <div>
              <Label>Alt text</Label>
              <Input value={data.alt_text} onChange={(e) => set('alt_text', e.target.value)} placeholder="EWRF Charity Golf 2026 …" data-testid="banner-alt-input" />
            </div>

            <div>
              <Label>Click-through URL (optional)</Label>
              <Input value={data.link_url} onChange={(e) => set('link_url', e.target.value)} placeholder="https://…" data-testid="banner-link-input" />
              <p className="text-xs text-gray-500 mt-1">If set, clicking the banner opens this link in a new tab.</p>
            </div>

            <Button onClick={save} disabled={loading} className="bg-[#1565C0]" data-testid="save-banner-btn">
              <Save className="w-4 h-4 mr-2" />{loading ? 'Saving…' : 'Save Banner'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#D4AF37]/30">
          <CardHeader><CardTitle className="text-base">Live Preview</CardTitle></CardHeader>
          <CardContent>
            {data.image ? (
              <img src={resolveImg(data.image)} alt="preview" className="w-full rounded border" />
            ) : (
              <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded text-gray-400 text-sm">
                No banner image
              </div>
            )}
            <p className="mt-3 text-xs text-gray-600">
              {data.published ? '✅ This banner is currently LIVE on the homepage.' : '⏸ Toggle Published ON to make this visible.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BannerManager;
